import {Attributes, AttributesRow, DataStatus} from 'platform/components';
import {Link} from 'platform/foundation';
import {useDateTimeFormatter, useFormatCurrency} from 'platform/locale';

import {isNil} from 'ramda';

import {useGetParticipationQuery} from '@dms/api/core';
import {
  useGetCashReceiptV4Query,
  useGetCashRegisterQuery,
  useRenderExpenseCashReceiptDocumentMutation,
  useRenderIncomeCashReceiptDocumentMutation,
} from '@dms/api/finance';
import {useGetBusinessCaseQuery} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {VehicleReservationDepositResponseBody} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';

import {buildArray, convertStringToCamelCase, openFile, parseDate} from 'shared/web';

import {usePermissions} from '../../../hooks/usePermissions/usePermissions';
import {handleApiError} from '../../../utils/handleApiError';
import {hasAclDocsWithPermission} from '../../../utils/hasAclDocsWithPermission';

interface RefundedReservationDepositDetailProps {
  refundedAdvanceDeposit: VehicleReservationDepositResponseBody;
  businessCaseId?: string;
}

export function RefundedReservationDepositDetail(props: RefundedReservationDepositDetailProps) {
  const [renderIncomeDocument] = useRenderIncomeCashReceiptDocumentMutation();
  const [renderExpenseDocument] = useRenderExpenseCashReceiptDocumentMutation();
  const formatCurrency = useFormatCurrency();
  const formatDateTime = useDateTimeFormatter();

  const {data: businessCase} = useGetBusinessCaseQuery(
    {businessCaseId: props.businessCaseId ?? ''},
    {skip: isNil(props.businessCaseId)}
  );
  const {data: businessCaseParticipation} = useGetParticipationQuery(
    {
      recordId: props.businessCaseId ?? '',
      resourceId: EntityResourceIds.businessCase,
    },
    {skip: isNil(props.businessCaseId)}
  );

  const {
    data: incomeCashRegisterDocument,
    isLoading: incomeCashRegisterDocumentLoading,
    isError: incomeCashRegisterDocumentError,
  } = useGetCashReceiptV4Query({
    cashRegisterDocumentId: props.refundedAdvanceDeposit.incomeCashReceiptId,
  });

  const {
    data: incomeCashRegister,
    isLoading: incomeCashRegisterLoading,
    isError: incomeCashRegisterError,
  } = useGetCashRegisterQuery(
    {
      cashRegisterId: incomeCashRegisterDocument?.cashRegisterId ?? '',
    },
    {skip: !incomeCashRegisterDocument?.cashRegisterId}
  );

  const {
    data: expenseCashRegisterDocument,
    isLoading: expenseCashRegisterDocumentLoading,
    isError: expenseCashRegisterDocumentError,
  } = useGetCashReceiptV4Query({
    cashRegisterDocumentId: props.refundedAdvanceDeposit.expenseCashReceiptId ?? '',
  });

  const [canReadIncomeCashReceiptDocument, canReadExpenseCashReceiptDocument] = usePermissions({
    permissionKeys: ['readIncomeCashReceiptDocument', 'readExpenseCashReceiptDocument'],
    scopes: {
      readIncomeCashReceiptDocument: {
        participation: businessCaseParticipation,
        branchId: businessCase?.branchId,
      },
      readExpenseCashReceiptDocument: {
        participation: businessCaseParticipation,
        branchId: businessCase?.branchId,
      },
    },
  });

  const isError =
    incomeCashRegisterDocumentError || incomeCashRegisterError || expenseCashRegisterDocumentError;

  const isLoading =
    incomeCashRegisterDocumentLoading ||
    incomeCashRegisterLoading ||
    expenseCashRegisterDocumentLoading;

  const isEmpty =
    !incomeCashRegister || !incomeCashRegisterDocument || !expenseCashRegisterDocument;

  const handleGenerateIncomePDF = async () => {
    await renderIncomeDocument({
      renderDocumentRequestBody: {
        cashRegisterDocumentId: props.refundedAdvanceDeposit.incomeCashReceiptId,
      },
    })
      .unwrap()
      .then((documentUrl) => {
        if (documentUrl) {
          openFile(documentUrl.pdfUrl);
        }
      })
      .catch(handleApiError);
  };

  const handleGenerateExpensePDF = async () => {
    await renderExpenseDocument({
      renderDocumentRequestBody: {
        cashRegisterDocumentId: props.refundedAdvanceDeposit.expenseCashReceiptId ?? '',
      },
    })
      .unwrap()
      .then((documentUrl) => {
        if (documentUrl) {
          openFile(documentUrl.pdfUrl);
        }
      })
      .catch(handleApiError);
  };

  if (isLoading && isEmpty) {
    return <DataStatus isLoading />;
  }

  const attributesRows = buildArray<AttributesRow>()
    .when(incomeCashRegisterDocument, {
      label: i18n.t('general.labels.amount'),
      value: formatCurrency(
        Number(incomeCashRegisterDocument!.totalPriceWithVat.amount),
        incomeCashRegisterDocument!.totalPriceWithVat.currency,
        2
      ),
    })
    .when(incomeCashRegisterDocument, {
      label: i18n.t('entity.accounting.labels.paymentMethod'),
      value: i18n.t(
        `entity.invoice.paymentMethod.${convertStringToCamelCase(
          incomeCashRegisterDocument!.paymentMethod
        )}`
      ),
    })
    .when(incomeCashRegisterDocument, {
      label: i18n.t('general.labels.issuedOn'),
      value: formatDateTime(
        'dateShort',
        parseDate(incomeCashRegisterDocument!.issuedAt) || new Date()
      ),
    })
    .when(incomeCashRegister, {
      label: i18n.t('general.labels.cashRegister'),
      value: incomeCashRegister!.name,
    })
    .when(incomeCashRegisterDocument, {
      label: i18n.t('general.labels.note'),
      value: incomeCashRegisterDocument!.note,
    })
    .when(incomeCashRegisterDocument, {
      label: i18n.t('entity.vehicle.labels.incomeDocument'),
      content: (
        <Link
          size="xSmall"
          isDisabled={!hasAclDocsWithPermission(canReadIncomeCashReceiptDocument)}
          onClick={handleGenerateIncomePDF}
          data-testid={testIds.accounting.cashReceiptDetail('generatePdf')}
          title={`${incomeCashRegisterDocument!.number}_${i18n.t(
            'entity.vehicle.labels.incomeDocument'
          )}`}
        />
      ),
    })
    .when(expenseCashRegisterDocument, {
      label: i18n.t('entity.vehicle.labels.expenseDocument'),
      content: (
        <Link
          size="xSmall"
          isDisabled={!hasAclDocsWithPermission(canReadExpenseCashReceiptDocument)}
          onClick={handleGenerateExpensePDF}
          data-testid={testIds.accounting.cashReceiptDetail('generateExpensePdf')}
          title={`${expenseCashRegisterDocument!.number}_${i18n.t(
            'entity.vehicle.labels.expenseDocument'
          )}`}
        />
      ),
    });

  return (
    <DataStatus isLoading={isLoading} isError={isError} isEmpty={isEmpty}>
      <Attributes rows={attributesRows} />
    </DataStatus>
  );
}
